import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Card, Col, Empty, Modal, Row, Table, Tag, Spin, Alert, Tooltip } from "antd";
import { FileDoneOutlined, RightOutlined, LoadingOutlined, ReloadOutlined, EyeOutlined } from "@ant-design/icons";

import MeuDpo from "../../../assets/images/meudpo.png";
import { TaskType } from "../../../context/taskContext";
import { GlobalContext } from "../../../context/globalContext";
import { GlobalContextType } from "../../../@types/global";
import BackHeader from "../../../components/BackHeader/BackHeader";
import CriarTarefa from "../../../components/Tarefas/CriarTarefa/CriarTarefa";
import Task from "../../../components/Tarefas/Task/Task";

// Importar os services atualizados
import DashboardService from "../../../services/dashboard/dashboard.service";
import CPPDService from "../../../services/dashboard/cppd.service";
import FornecedoresService from "../../../services/dashboard/fornecedores.service";
import CSAService from "../../../services/dashboard/csa.service"; // Adicionar CSA Service
import MapeamentosService from "../../../services/dashboard/mapeamentos.service"; // Adicionar Mapeamentos Service

// Importar Interfaces
import { DashboardStats, StatusMapping, TarefaDashboard } from "../../../types/dashboard/dashboard.types";

// Interfaces para CSA e Mapeamentos - ATUALIZADA SEM CRÍTICAS
interface CSAStats {
  totalVulnerabilidades: number;
  vulnerabilidadesAltas: number;
  vulnerabilidadesMedias: number;
  vulnerabilidadesBaixas: number;
  scoreMediaCVSS: number;
  hostsUnicos: number;
  ultimaAtualizacao: string | null;
}

interface MapeamentosStats {
  totalQuestionarios: number;
  questionariosAtivos: number;
  questionariosConcluidos: number;
  totalTitulares: number;
  totalProcessos: number;
  taxaConclusao: number;
  areasCobertas: number;
}

const DashboardInicial = () => {
  const {
    windowWidth,
    grauRiscosPergunta,
    moduloMapeamento,
    moduloProcessos,
    moduloTask,
    userInfo,
    currentCliente,
  } = React.useContext(GlobalContext) as GlobalContextType;
  
  const navigate = useNavigate();
  const location = useLocation();

  // Estados do componente original
  const [showModalQuestionario, setShowModalQuestionario] = React.useState(false);
  const [showCriarTarefa, setShowCriarTarefa] = React.useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);

  // Estados para dados dinâmicos do dashboard consolidado
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  
  // NOVOS ESTADOS PARA CSA E MAPEAMENTOS
  const [csaStats, setCSAStats] = useState<CSAStats | null>(null);
  const [mapeamentosStats, setMapeamentosStats] = useState<MapeamentosStats | null>(null);
  const [loadingCSA, setLoadingCSA] = useState(false);
  const [loadingMapeamentos, setLoadingMapeamentos] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [tarefasCPPD, setTarefasCPPD] = useState<TarefaDashboard[]>([]);

  // Função para obter o cliente_id de múltiplas fontes
  const getClienteId = (): string | null => {
    try {
      // 1. PRIORIDADE: Cliente selecionado no contexto global (Drawer)
      if (currentCliente?._id) {
        console.log('Cliente ID obtido do contexto (selecionado no drawer):', currentCliente._id);
        return currentCliente._id;
      }
      
      // 2. Fallback: Tentar obter do userInfo
      if (userInfo?.cliente_id) {
        console.log('Cliente ID obtido do userInfo:', userInfo.cliente_id);
        return userInfo.cliente_id;
      }
      
      // 3. Tentar obter do localStorage
      const userInfoString = localStorage.getItem('userInfo');
      if (userInfoString) {
        try {
          const parsedUserInfo = JSON.parse(userInfoString);
          if (parsedUserInfo?.cliente_id) {
            console.log('Cliente ID obtido do localStorage userInfo:', parsedUserInfo.cliente_id);
            return parsedUserInfo.cliente_id;
          }
        } catch (parseError) {
          console.warn('Erro ao parsear userInfo do localStorage:', parseError);
        }
      }
      
      // 4. Tentar obter do currentCliente no localStorage
      const currentClienteString = localStorage.getItem('currentCliente');
      if (currentClienteString) {
        try {
          const currentCliente = JSON.parse(currentClienteString);
          if (currentCliente?._id) {
            console.log('Cliente ID obtido do currentCliente localStorage:', currentCliente._id);
            return currentCliente._id;
          }
        } catch (parseError) {
          console.warn('Erro ao parsear currentCliente do localStorage:', parseError);
        }
      }
      
      // 5. SessionStorage como fallback
      const sessionClienteId = sessionStorage.getItem('cliente_id');
      if (sessionClienteId) {
        console.log('Cliente ID obtido do sessionStorage:', sessionClienteId);
        return sessionClienteId;
      }
      
      // 6. URL params como último recurso
      const urlParams = new URLSearchParams(window.location.search);
      const clienteIdFromUrl = urlParams.get('cliente_id');
      if (clienteIdFromUrl) {
        console.log('Cliente ID obtido da URL:', clienteIdFromUrl);
        sessionStorage.setItem('cliente_id', clienteIdFromUrl);
        return clienteIdFromUrl;
      }
      
      console.error('❌ Cliente ID não encontrado em nenhuma fonte disponível');
      return null;
    } catch (error) {
      console.error('❌ Erro crítico ao obter cliente ID:', error);
      return null;
    }
  };

  // NOVA FUNÇÃO: Carregar dados do CSA - ATUALIZADA
  const loadCSAData = async (clienteId: string) => {
    try {
      setLoadingCSA(true);
      console.log('🔒 Carregando dados CSA para cliente:', clienteId);
      
      const csaResponse = await CSAService.getDashboardStats(clienteId);
      
      if (csaResponse && csaResponse.estatisticasGerais) {
        const csaData: CSAStats = {
          totalVulnerabilidades: csaResponse.estatisticasGerais.totalVulnerabilidades || 0,
          vulnerabilidadesAltas: csaResponse.analisePorSeveridade?.find(s => s.severity === 'high')?.count || 0,
          vulnerabilidadesMedias: csaResponse.analisePorSeveridade?.find(s => s.severity === 'medium')?.count || 0,
          vulnerabilidadesBaixas: csaResponse.analisePorSeveridade?.find(s => s.severity === 'low')?.count || 0,
          scoreMediaCVSS: parseFloat(csaResponse.estatisticasGerais.scoreMediaCVSS) || 0,
          hostsUnicos: csaResponse.estatisticasGerais.hostsUnicos || 0,
          ultimaAtualizacao: csaResponse.ultimaAtualizacao || null
        };
        
        setCSAStats(csaData);
        console.log('✅ Dados CSA carregados:', csaData);
      } else {
        // Dados padrão se não houver resposta válida
        setCSAStats({
          totalVulnerabilidades: 0,
          vulnerabilidadesAltas: 0,
          vulnerabilidadesMedias: 0,
          vulnerabilidadesBaixas: 0,
          scoreMediaCVSS: 0,
          hostsUnicos: 0,
          ultimaAtualizacao: null
        });
        console.log('⚠️ Sem dados CSA disponíveis');
      }
    } catch (error) {
      console.error('❌ Erro ao carregar dados CSA:', error);
      // Dados padrão em caso de erro
      setCSAStats({
        totalVulnerabilidades: 0,
        vulnerabilidadesAltas: 0,
        vulnerabilidadesMedias: 0,
        vulnerabilidadesBaixas: 0,
        scoreMediaCVSS: 0,
        hostsUnicos: 0,
        ultimaAtualizacao: null
      });
    } finally {
      setLoadingCSA(false);
    }
  };

  // NOVA FUNÇÃO: Carregar dados de Mapeamentos
  const loadMapeamentosData = async (clienteId: string) => {
    try {
      setLoadingMapeamentos(true);
      console.log('🗺️ Carregando dados de Mapeamentos para cliente:', clienteId);
      
      const mapeamentosResponse = await MapeamentosService.obterEstatisticas(clienteId);
      
      if (mapeamentosResponse && mapeamentosResponse.success && mapeamentosResponse.data) {
        const mapeamentosData: MapeamentosStats = {
          totalQuestionarios: mapeamentosResponse.data.total_questionarios || 0,
          questionariosAtivos: mapeamentosResponse.data.questionarios_concluidos || 0,
          questionariosConcluidos: mapeamentosResponse.data.questionarios_concluidos || 0,
          totalTitulares: mapeamentosResponse.data.total_titulares || 0,
          totalProcessos: mapeamentosResponse.data.total_processos || 0,
          taxaConclusao: mapeamentosResponse.data.taxa_conclusao || 0,
          areasCobertas: mapeamentosResponse.data.areas_mapeadas || 0
        };
        
        setMapeamentosStats(mapeamentosData);
        console.log('✅ Dados Mapeamentos carregados:', mapeamentosData);
      } else {
        // Dados padrão se não houver resposta válida
        setMapeamentosStats({
          totalQuestionarios: 0,
          questionariosAtivos: 0,
          questionariosConcluidos: 0,
          totalTitulares: 0,
          totalProcessos: 0,
          taxaConclusao: 0,
          areasCobertas: 0
        });
        console.log('⚠️ Sem dados de Mapeamentos disponíveis');
      }
    } catch (error) {
      console.error('❌ Erro ao carregar dados de Mapeamentos:', error);
      // Dados padrão em caso de erro
      setMapeamentosStats({
        totalQuestionarios: 0,
        questionariosAtivos: 0,
        questionariosConcluidos: 0,
        totalTitulares: 0,
        totalProcessos: 0,
        taxaConclusao: 0,
        areasCobertas: 0
      });
    } finally {
      setLoadingMapeamentos(false);
    }
  };

  // Função principal para carregar dados do dashboard consolidado
  const loadDashboardData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
        console.log('🔄 Atualizando dados do dashboard...');
      } else {
        setLoading(true);
        console.log('🔄 Carregando dados iniciais do dashboard...');
      }
      setError(null);
      
      const clienteId = getClienteId();
      if (!clienteId) {
        throw new Error('ID do cliente não disponível. Verifique se você está logado corretamente.');
      }

      console.log('📊 Iniciando carregamento dos dados para cliente:', clienteId);

      // USAR DIRETAMENTE OS SERVICES QUE FUNCIONAM + NOVOS SERVICES CSA E MAPEAMENTOS
      try {
        console.log('🎯 Carregando dados via services individuais (que funcionam)...');
        
        // Carregar dados em paralelo usando os services que funcionam
        const [cppdResult, fornecedoresResult, maturidadeResult, tarefasResult] = await Promise.allSettled([
          // Dados do CPPD
          CPPDService.getDashboardData({ cliente_id: clienteId }),
          
          // Dados dos fornecedores
          FornecedoresService.getStats(clienteId),
          
          // Dados de maturidade
          FornecedoresService.getMaturidade(clienteId),
          
          // Tarefas do CPPD
          DashboardService.getMinhasTarefas(userInfo?._id, 5, clienteId)
        ]);

        // CARREGAR CSA E MAPEAMENTOS EM PARALELO (SEM BLOQUEAR O DASHBOARD)
        Promise.allSettled([
          loadCSAData(clienteId),
          loadMapeamentosData(clienteId)
        ]).then(() => {
          console.log('✅ Dados auxiliares (CSA e Mapeamentos) carregados');
        }).catch(auxError => {
          console.warn('⚠️ Erro ao carregar dados auxiliares:', auxError);
        });

        // Processar resultados do CPPD
        let cppdData = {
          totalCPPDs: 0,
          totalReunioes: 0,
          reunioesPendentes: 0,
          reunioesEmAndamento: 0,
          reunioesConcluidas: 0,
          proximasReunioes: [],
          membrosCount: 0,
          estatisticasParticipacao: {
            mediaTaxaParticipacao: 0,
            mediaConvidados: 0,
            mediaPresentes: 0
          }
        };

        if (cppdResult.status === 'fulfilled' && cppdResult.value) {
          const data = cppdResult.value as any;
          cppdData = {
            totalCPPDs: data.totalCPPDs || 0,
            totalReunioes: data.totalReunioes || 0,
            reunioesPendentes: data.reunioesPendentes || 0,
            reunioesEmAndamento: data.reunioesEmAndamento || 0,
            reunioesConcluidas: data.reunioesConcluidas || 0,
            proximasReunioes: data.proximasReunioes || [],
            membrosCount: data.membrosData?.length || 0,
            estatisticasParticipacao: data.estatisticasParticipacao || {
              mediaTaxaParticipacao: 0,
              mediaConvidados: 0,
              mediaPresentes: 0
            }
          };
          
          console.log('✅ Dados CPPD carregados com sucesso:', {
            totalCPPDs: cppdData.totalCPPDs,
            totalReunioes: cppdData.totalReunioes,
            proximasReunioes: cppdData.proximasReunioes?.length || 0
          });
        } else {
          console.warn('⚠️ Falha ao carregar dados CPPD:', cppdResult.status === 'rejected' ? cppdResult.reason?.message : 'Dados indisponíveis');
        }

        // Processar resultados dos fornecedores
        let fornecedoresData = {
          totalCadastros: 0,
          questionariosEnviados: 0,
          questionariosRespondidos: 0,
          enviosPendentes: 0,
          planosDeAcao: 0,
          questionariosNaoRespondidos: 0
        };

        if (fornecedoresResult.status === 'fulfilled' && fornecedoresResult.value) {
          const data = fornecedoresResult.value as any;
          fornecedoresData = {
            totalCadastros: data.totalCadastros || 0,
            questionariosEnviados: data.questionariosEnviados || 0,
            questionariosRespondidos: data.questionariosRespondidos || 0,
            enviosPendentes: data.enviosPendentes || 0,
            planosDeAcao: data.planosDeAcao || 0,
            questionariosNaoRespondidos: data.questionariosNaoRespondidos || 0
          };
          
          console.log('✅ Dados Fornecedores carregados com sucesso:', {
            totalCadastros: fornecedoresData.totalCadastros,
            questionariosRespondidos: fornecedoresData.questionariosRespondidos
          });
        } else {
          console.warn('⚠️ Falha ao carregar dados de fornecedores:', fornecedoresResult.status === 'rejected' ? fornecedoresResult.reason?.message : 'Dados indisponíveis');
        }

        // Processar resultados da maturidade
        let maturidadeData = {
          percentualMaturidade: 0,
          nivelConformidade: 'baixo' as const
        };

        if (maturidadeResult.status === 'fulfilled' && maturidadeResult.value) {
          const data = maturidadeResult.value as any;
          maturidadeData = {
            percentualMaturidade: data.percentualMaturidade || 0,
            nivelConformidade: data.nivelConformidade || 'baixo'
          };
          
          console.log('✅ Dados Maturidade carregados com sucesso:', {
            percentualMaturidade: maturidadeData.percentualMaturidade,
            nivelConformidade: maturidadeData.nivelConformidade
          });
        } else {
          console.warn('⚠️ Falha ao carregar dados de maturidade:', maturidadeResult.status === 'rejected' ? maturidadeResult.reason?.message : 'Dados indisponíveis');
        }

        // Processar tarefas
        let tarefasCPPDData = [];
        if (tarefasResult.status === 'fulfilled' && tarefasResult.value) {
          tarefasCPPDData = tarefasResult.value as any[];
          setTarefasCPPD(tarefasCPPDData);
        } else {
          console.warn('⚠️ Não foi possível carregar tarefas CPPD:', tarefasResult.status === 'rejected' ? tarefasResult.reason?.message : 'Dados indisponíveis');
          setTarefasCPPD([]);
        }

        // Compilar dados consolidados do dashboard
        const consolidatedStats: DashboardStats = {
          cppd: {
            totalCPPDs: cppdData.totalCPPDs,
            totalReunioes: cppdData.totalReunioes,
            reunioesPendentes: cppdData.reunioesPendentes,
            reunioesEmAndamento: cppdData.reunioesEmAndamento,
            reunioesConcluidas: cppdData.reunioesConcluidas,
            proximasReunioes: cppdData.proximasReunioes,
            membrosCount: cppdData.membrosCount,
            estatisticasParticipacao: cppdData.estatisticasParticipacao
          },
          fornecedores: {
            totalCadastros: fornecedoresData.totalCadastros,
            questionariosEnviados: fornecedoresData.questionariosEnviados,
            questionariosRespondidos: fornecedoresData.questionariosRespondidos,
            enviosPendentes: fornecedoresData.enviosPendentes,
            planosDeAcao: fornecedoresData.planosDeAcao,
            questionariosNaoRespondidos: fornecedoresData.questionariosNaoRespondidos,
            percentualMaturidade: maturidadeData.percentualMaturidade,
            nivelConformidade: maturidadeData.nivelConformidade
          }
        };

        setDashboardStats(consolidatedStats);
        setLastUpdate(new Date());
        
        console.log('✅ Dashboard carregado com sucesso via services individuais:', {
          cppd: {
            total: consolidatedStats.cppd.totalCPPDs,
            reunioes: consolidatedStats.cppd.totalReunioes,
            membros: consolidatedStats.cppd.membrosCount
          },
          fornecedores: {
            total: consolidatedStats.fornecedores.totalCadastros,
            maturidade: `${consolidatedStats.fornecedores.percentualMaturidade}%`
          }
        });

      } catch (error) {
        console.error('❌ Erro ao carregar dados via services individuais:', error);
        throw error;
      }

    } catch (error) {
      console.error('❌ Erro crítico ao carregar dados do dashboard:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido ao carregar dados do dashboard';
      setError(errorMessage);
      
      // Se não conseguiu carregar nada, definir dados vazios para evitar crash
      if (!dashboardStats) {
        setDashboardStats({
          cppd: {
            totalCPPDs: 0,
            totalReunioes: 0,
            reunioesPendentes: 0,
            reunioesEmAndamento: 0,
            reunioesConcluidas: 0,
            proximasReunioes: [],
            membrosCount: 0,
            estatisticasParticipacao: {
              mediaTaxaParticipacao: 0,
              mediaConvidados: 0,
              mediaPresentes: 0
            }
          },
          fornecedores: {
            totalCadastros: 0,
            questionariosEnviados: 0,
            questionariosRespondidos: 0,
            enviosPendentes: 0,
            planosDeAcao: 0,
            questionariosNaoRespondidos: 0,
            percentualMaturidade: 0,
            nivelConformidade: 'baixo'
          }
        });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Função para refresh manual dos dados
  const handleRefresh = async () => {
    console.log('🔄 Refresh manual solicitado pelo usuário');
    await loadDashboardData(true);
  };

  // Carregar dados ao montar o componente ou quando userInfo mudar
  useEffect(() => {
    const clienteId = getClienteId();
    if (clienteId) {
      console.log('🔄 Cliente mudou, recarregando dashboard para:', clienteId);
      loadDashboardData();
    }
  }, [userInfo, currentCliente]);

  // Auto-refresh a cada 5 minutos para manter dados atualizados
  useEffect(() => {
    // Só fazer auto-refresh se a página estiver visível
    const handleVisibilityChange = () => {
      if (!document.hidden && dashboardStats) {
        loadDashboardData(true);
      }
    };

    const interval = setInterval(() => {
      if (!document.hidden) {
        console.log('🔄 Auto-refresh automático do dashboard (5min)');
        loadDashboardData(true);
      }
    }, 5 * 60 * 1000);

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dashboardStats]);

  // Effect original do componente para modal de questionário
  React.useEffect(() => {
    const desiredRoute = localStorage.getItem("@desiredroute");
    if (desiredRoute && desiredRoute.includes("responder")) {
      setShowModalQuestionario(true);
    }
  }, [location]);

  // Mapeamento de status para cores e textos
  const statusMapping: StatusMapping = {
    'Pendente': { color: 'orange', text: 'Pendente' },
    'Em andamento': { color: 'blue', text: 'Em andamento' },
    'Realizada': { color: 'green', text: 'Concluído' },
    'Concluído': { color: 'green', text: 'Concluído' },
    'Concluída': { color: 'green', text: 'Concluído' }
  };

  // Função para obter informações de status
  const getStatusInfo = (status: string) => {
    return statusMapping[status] || { color: 'default', text: status };
  };

  // Função para obter cor baseada no nível de conformidade
  const getConformidadeColor = (nivel: string): string => {
    switch (nivel) {
      case 'alto': return 'green';
      case 'medio': return 'orange';
      case 'baixo': return 'red';
      default: return 'red';
    }
  };

  // Função para obter texto do nível de conformidade
  const getConformidadeText = (nivel: string): string => {
    switch (nivel) {
      case 'alto': return 'Alta';
      case 'medio': return 'Média';
      case 'baixo': return 'Baixa';
      default: return 'Baixa';
    }
  };

  // FUNÇÕES ATUALIZADAS: Para CSA (focando na soma de vulnerabilidades relevantes)
  const getCSAStatusColor = (totalRelevantes: number, altas: number): string => {
    if (altas > 10) return 'red';           // Muitas vulnerabilidades altas = crítico
    if (altas > 3) return 'orange';         // Algumas vulnerabilidades altas = atenção
    if (totalRelevantes > 50) return 'orange'; // Muitas vulnerabilidades relevantes = ativo
    if (totalRelevantes > 0) return 'blue';    // Algumas vulnerabilidades = monitoramento
    return 'green';                         // Nenhuma vulnerabilidade = seguro
  };

  const getCSAStatusText = (totalRelevantes: number, altas: number): string => {
    if (altas > 10) return 'Crítico';
    if (altas > 3) return 'Atenção';
    if (totalRelevantes > 50) return 'Ativo';
    if (totalRelevantes > 0) return 'Monitoramento';
    return 'Seguro';
  };

  const getMapeamentosStatusColor = (taxaConclusao: number): string => {
    if (taxaConclusao >= 90) return 'green';
    if (taxaConclusao >= 60) return 'blue';
    if (taxaConclusao >= 30) return 'orange';
    return 'red';
  };

  const getMapeamentosStatusText = (taxaConclusao: number): string => {
    if (taxaConclusao >= 90) return 'Completo';
    if (taxaConclusao >= 60) return 'Avançado';
    if (taxaConclusao >= 30) return 'Em andamento';
    return 'Iniciado';
  };

  // Função auxiliar para calcular total de vulnerabilidades relevantes
  const calcularVulnerabilidadesRelevantes = (csaStats: CSAStats | null): number => {
    if (!csaStats) return 0;
    return safeGet(csaStats.vulnerabilidadesAltas, 0) + 
           safeGet(csaStats.vulnerabilidadesMedias, 0) + 
           safeGet(csaStats.vulnerabilidadesBaixas, 0);
  };

  // Função para calcular percentual de conclusão
  const calcularPercentualConclusao = (concluidos: number, total: number): number => {
    if (total === 0) return 0;
    return Math.round((concluidos / total) * 100);
  };

  // Safe accessor para dados opcionais
  const safeGet = <T,>(value: T | undefined, defaultValue: T): T => {
    return value !== undefined ? value : defaultValue;
  };

  // NOVA FUNÇÃO: Determinar origem da tarefa com tags
  const determinarOrigemTarefa = (tarefa: any) => {
    if (tarefa.tipo === 'reuniao_cppd') {
      return { 
        tag: "CPPD", 
        color: "#722ed1", 
        icon: "📊",
        description: "Comitê de Proteção de Dados"
      };
    }
    
    if (tarefa.tipo === 'fornecedor_questionario') {
      return { 
        tag: "FORNECEDOR", 
        color: "#1890ff", 
        icon: "📋",
        description: "Gestão de Fornecedores"
      };
    }
    
    const title = tarefa.title.toLowerCase();
    
    if (title.includes("vulnerab") || title.includes("pentest")) {
      return { 
        tag: "CSA", 
        color: "#faad14", 
        icon: "🛡️",
        description: "Cyber Security Assessment"
      };
    }
    
    if (title.includes("cve") || title.includes("adius") || title.includes("nessus")) {
      return { 
        tag: "SEGURANÇA", 
        color: "#f5222d", 
        icon: "⚠️",
        description: "Vulnerabilidades de Segurança"
      };
    }
    
    if (title.includes("mapeamento") || title.includes("lgpd") || title.includes("titular")) {
      return { 
        tag: "MAPEAMENTO", 
        color: "#13c2c2", 
        icon: "🗺️",
        description: "Mapeamento de Processos"
      };
    }
    
    if (title.includes("chamado") || title.includes("ticket")) {
      return { 
        tag: "SUPORTE", 
        color: "#eb2f96", 
        icon: "🎫",
        description: "Chamados de Suporte"
      };
    }
    
    return { 
      tag: "GERAL", 
      color: "#52c41a", 
      icon: "⬡",
      description: "Tarefa Geral"
    };
  };
 // Preparar dados de tarefas combinando dados do módulo com tarefas CPPD
 const todasTarefas = [
   ...tarefasCPPD,
   ...moduloTask.tasks
     .filter((t: any) => !t.title.includes("Pergunta: ") && t.assignment_id === userInfo._id)
     .map((task: any) => ({
       ...task,
       tipo: task.tipo || 'tarefa_geral' as const,
       codigo: task.title.includes("CVE") ? task.title.split("CVE")[1]?.split(" ")[0] : "",
     }))
 ].slice(0, 10); // Limitar a 10 tarefas

 // Colunas para a tabela "Minhas tarefas" - ATUALIZADA com tags de origem
 const tarefasColumns = [
   {
     title: "Nome",
     dataIndex: "title",
     key: "title",
     render: (text: string, record: any) => {
       const origem = determinarOrigemTarefa(record);
       
       return (
         <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
           {/* Tag de identificação da origem */}
           <Tooltip title={origem.description}>
             <Tag 
               color={origem.color}
               style={{ 
                 margin: 0,
                 fontSize: '10px',
                 padding: '2px 6px',
                 borderRadius: '4px',
                 fontWeight: 600,
                 minWidth: '70px',
                 textAlign: 'center',
                 cursor: 'help'
               }}
             >
               {origem.tag}
             </Tag>
           </Tooltip>
           
           {/* Ícone e título da tarefa */}
           <span style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
             <span style={{ marginRight: 8, fontSize: "16px" }}>
               {origem.icon}
             </span>
             <span style={{ 
               overflow: 'hidden',
               textOverflow: 'ellipsis',
               whiteSpace: 'nowrap',
               maxWidth: '300px'
             }}>
               {text}
             </span>
           </span>
         </div>
       );
     },
   },
   {
     title: "Código",
     dataIndex: "codigo",
     key: "codigo",
     render: (text: string, record: any) => {
       if (record.tipo === 'reuniao_cppd') return "CPPD";
       if (record.tipo === 'fornecedor_questionario') return "FORN";
       
       const title = record.title.toLowerCase();
       if (title.includes("vulnerabilidade")) {
         return "192.168.0.11";
       } else if (title.includes("adius")) {
         return "CVE-2023-2100";
       } else if (title.includes("nessus")) {
         return "CVE-2023-3359";
       }
       return text || "";
     },
   },
   {
     title: "Status",
     dataIndex: "status",
     key: "status",
     render: (status: string) => {
       const statusInfo = getStatusInfo(status || "Pendente");
       return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
     },
   },
   {
     title: "Vencimento",
     dataIndex: "prazo_conclusao",
     key: "prazo_conclusao",
     render: (prazo: any, record: any) => {
       if (record.tipo === 'reuniao_cppd' && record.prazo_conclusao) {
         return new Date(record.prazo_conclusao).toLocaleDateString('pt-BR');
       }
       
       const title = record.title.toLowerCase();
       if (title.includes("nessus")) {
         return "16/10/24";
       } else if (title.includes("vulnerabilidade")) {
         return record.status === "Concluída" ? "16/10/24" : "Hoje";
       } else if (title.includes("adius")) {
         return "Amanhã";
       }
       return "Hoje";
     },
   },
   {
     title: "",
     key: "action",
     render: (record: any) => (
       <div style={{ display: 'flex', gap: 8 }}>
         {record.tipo === 'reuniao_cppd' ? (
           <Button 
             type="primary"
             size="small"
             icon={<EyeOutlined />} 
             style={{ backgroundColor: "#722ed1", 
               borderColor: "#722ed1",
               fontSize: "12px",
               height: "28px"
             }}
             onClick={() => navigate("/dashboard/governanca/cppd")}
           >
             Ver CPPD
           </Button>
         ) : (
           <Button 
             type="primary"
             size="small"
             icon={<EyeOutlined />}
             style={{ 
               backgroundColor: "#722ed1", 
               borderColor: "#722ed1",
               fontSize: "12px",
               height: "28px"
             }}
             onClick={() => {
               moduloTask.setSelectedTask(record);
               handleShowModal();
             }}
           >
             Ver tarefa
           </Button>
         )}
       </div>
     ),
   },
 ];

 const handleShowModal = () => {
   setShowModal(!showModal);
 };
 
 const handleShowCriarTarefa = () => {
   setShowCriarTarefa(!showCriarTarefa);
 };

 // Colunas para a tabela de chamados (mantendo implementação original)
 const chamadosColumns = [
   {
     title: "Número",
     dataIndex: "numero",
     key: "numero",
     render: (text: string) => <span style={{ color: "#888" }}>{text}</span>,
   },
   {
     title: "Assunto",
     dataIndex: "title",
     key: "title",
   },
   {
     title: "Data/Horário",
     dataIndex: "created_at",
     key: "created_at",
     render: (date: string) => {
       return new Date(date).toLocaleString('pt-BR', {
         day: '2-digit',
         month: '2-digit',
         year: '2-digit',
         hour: '2-digit',
         minute: '2-digit'
       });
     },
   },
   {
     title: "Cliente",
     dataIndex: "cliente",
     key: "cliente",
     render: () => userInfo?.nome || "Cliente",
   },
   {
     title: "Status",
     dataIndex: "status",
     key: "status",
     render: (status: string) => {
       const statusInfo = getStatusInfo(status || "Pendente");
       return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
     },
   },
   {
     title: "Ação",
     key: "action",
     render: (record: any) => (
       <span style={{ color: "#888" }}>
         Visualizar
       </span>
     ),
   },
 ];

 // Preparar dados de chamados com números
 const chamadosWithNumbers = moduloTask.tasks
   ?.filter((task: any) => task.tipo === "chamado")
   .map((task: any, index: number) => ({
     ...task,
     numero: `#00${124 + index}`,
   }));

 // Renderizar loading
 if (loading) {
   return (
     <div className="dashboard-inicial" style={{ padding: "0 24px" }}>
       <BackHeader title="Visão geral" />
       <div style={{ 
         display: 'flex', 
         justifyContent: 'center', 
         alignItems: 'center', 
         height: '400px',
         flexDirection: 'column'
       }}>
         <Spin 
           size="large" 
           indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
         />
         <p style={{ marginTop: 16, color: '#666' }}>
           Carregando dados do dashboard...
         </p>
       </div>
     </div>
   );
 }

 // Renderizar erro crítico (sem dados)
 if (error && !dashboardStats) {
   return (
     <div className="dashboard-inicial" style={{ padding: "0 24px" }}>
       <BackHeader title="Visão geral" />
       <Alert
         message="Erro ao carregar dados do dashboard"
         description={error}
         type="error"
         showIcon
         style={{ marginBottom: 24 }}
         action={
           <Button size="small" danger onClick={() => loadDashboardData()}>
             Tentar novamente
           </Button>
         }
       />
     </div>
   );
 }

 return (
   <div className="dashboard-inicial" style={{ padding: "0 24px" }}>
     {/* Header com botão de refresh */}
     <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
       <BackHeader title="Visão geral" />
       <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
         {lastUpdate && (
           <Tooltip title={`Última atualização: ${lastUpdate.toLocaleString('pt-BR')}`}>
             <span style={{ fontSize: '12px', color: '#666' }}>
               {lastUpdate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
             </span>
           </Tooltip>
         )}
         <Button 
           type="primary" 
           icon={<ReloadOutlined spin={refreshing} />}
           onClick={handleRefresh}
           loading={refreshing}
           title="Atualizar dados"
         />
       </div>
     </div>
     
     {/* Alerta de erro não crítico (dados parciais disponíveis) */}
     {error && dashboardStats && (
       <Alert
         message="Alguns dados podem estar desatualizados"
         description={error}
         type="warning"
         showIcon
         closable
         style={{ marginBottom: 16 }}
       />
     )}
     
     {/* Tabela de Minhas Tarefas */}
     <div style={{ marginBottom: "32px" }}>
       <h3 style={{ marginBottom: "16px", fontSize: "16px", fontWeight: 600 }}>
         Minhas tarefas
         {todasTarefas.length > 0 && (
           <span style={{ color: '#888', fontWeight: 400, marginLeft: 8 }}>
             ({todasTarefas.length})
           </span>
         )}
       </h3>
       <Table
         dataSource={todasTarefas}
         columns={tarefasColumns}
         pagination={false}
         locale={{
           emptyText: <Empty description="Nenhuma tarefa encontrada" />
         }}
         rowKey={(record) => record._id || record.id || Math.random()}
         size="middle"
       />
     </div>

     {/* Cards de Métricas Dinâmicas */}
     <Row gutter={[24, 24]} style={{ marginBottom: "32px" }}>
       {/* Coluna 1: CSA DINÂMICO e Maturidade */}
       <Col xs={24} sm={24} md={8} lg={8}>
         <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
           {/* Card CSA - ATUALIZADO PARA MOSTRAR SOMA DAS VULNERABILIDADES RELEVANTES */}
           <Card 
             style={{ 
               background: '#fff',
               borderRadius: '8px',
               cursor: 'pointer',
               transition: 'all 0.3s ease',
               border: '1px solid #e8e8e8',
               opacity: loadingCSA ? 0.7 : 1,
               minHeight: '180px',
             }}
             bodyStyle={{ padding: '16px' }}
             className="metric-card"
             hoverable
             onClick={() => navigate("/dashboard/governanca/csa")}
           >
             <div style={{ marginBottom: '2px' }}>
               <h4 style={{ margin: 0, fontWeight: 500, fontSize: '16px' }}>
                 CSA
                 {loadingCSA && <Spin size="small" style={{ marginLeft: 8 }} />}
               </h4>
               <span style={{ color: '#888', fontSize: '12px' }}>Cyber Security Assessment</span>
             </div>
             
             {/* Agora mostra soma das vulnerabilidades relevantes / total */}
             <div style={{ 
               fontSize: '44px', 
               color: '#5c4ab5', 
               fontWeight: 500,
               marginBottom: '8px',
               lineHeight: '1',
               display: 'flex',
               alignItems: 'baseline',
             }}>
               {calcularVulnerabilidadesRelevantes(csaStats)}
               <span style={{ fontSize: '24px', color: '#ccc' }}>
                 /{csaStats ? safeGet(csaStats.totalVulnerabilidades, 0) : 0}
               </span>
             </div>
             
             <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
               <Tag 
                 color={(() => {
                   if (!csaStats) return 'gray';
                   const totalRelevantes = calcularVulnerabilidadesRelevantes(csaStats);
                   return getCSAStatusColor(totalRelevantes, safeGet(csaStats.vulnerabilidadesAltas, 0));
                 })()}
                 style={{ 
                   borderRadius: '4px',
                   padding: '0px 8px',
                   margin: 0,
                   fontSize: '12px'
                 }}
               >
                 {(() => {
                   if (!csaStats) return 'Aguardando';
                   const totalRelevantes = calcularVulnerabilidadesRelevantes(csaStats);
                   return getCSAStatusText(totalRelevantes, safeGet(csaStats.vulnerabilidadesAltas, 0));
                 })()}
               </Tag>
               {csaStats && csaStats.hostsUnicos > 0 && (
                 <span style={{ fontSize: '12px', color: '#888' }}>
                   {csaStats.hostsUnicos} host{csaStats.hostsUnicos > 1 ? 's' : ''}
                 </span>
               )}
               {csaStats && csaStats.scoreMediaCVSS > 0 && (
                 <span style={{ fontSize: '12px', color: '#888' }}>
                   CVSS: {csaStats.scoreMediaCVSS.toFixed(1)}
                 </span>
               )}
             </div>
             
             {/* Breakdown das vulnerabilidades com números mais visíveis */}
             {csaStats && (csaStats.vulnerabilidadesAltas > 0 || csaStats.vulnerabilidadesMedias > 0 || csaStats.vulnerabilidadesBaixas > 0) ? (
               <div style={{ 
                 fontSize: '12px', 
                 color: '#666', 
                 display: 'flex',
                 gap: '12px',
                 flexWrap: 'wrap',
                 alignItems: 'center'
               }}>
                 {csaStats.vulnerabilidadesAltas > 0 && (
                   <span style={{ 
                     color: '#f5222d', 
                     fontWeight: 600,
                     display: 'flex', 
                     alignItems: 'center', 
                     gap: '4px' 
                   }}>
                     🔴 <strong>{csaStats.vulnerabilidadesAltas}</strong> altas
                   </span>
                 )}
                 {csaStats.vulnerabilidadesMedias > 0 && (
                   <span style={{ 
                     color: '#faad14', 
                     fontWeight: 600,
                     display: 'flex', 
                     alignItems: 'center', 
                     gap: '4px' 
                   }}>
                     🟡 <strong>{csaStats.vulnerabilidadesMedias}</strong> médias
                   </span>
                 )}
                 {csaStats.vulnerabilidadesBaixas > 0 && (
                   <span style={{ 
                     color: '#52c41a', 
                     fontWeight: 600,
                     display: 'flex', 
                     alignItems: 'center', 
                     gap: '4px' 
                   }}>
                     🟢 <strong>{csaStats.vulnerabilidadesBaixas}</strong> baixas
                   </span>
                 )}
               </div>
             ) : csaStats && csaStats.totalVulnerabilidades === 0 ? (
               <div style={{ 
                 fontSize: '12px', 
                 color: '#52c41a', 
                 fontWeight: 600,
                 textAlign: 'center',
                 padding: '8px',
                 backgroundColor: '#f6ffed',
                 borderRadius: '4px',
                 border: '1px solid #b7eb8f'
               }}>
                 ✅ Nenhuma vulnerabilidade encontrada
               </div>
             ) : (
               <div style={{ 
                 fontSize: '12px', 
                 color: '#666', 
                 textAlign: 'center',
                 fontStyle: 'italic'
               }}>
                 Carregando análise...
               </div>
             )}
           </Card>

           {/* Card Maturidade - dados fornecedores dinâmicos */}
           <Card 
             style={{ 
               background: '#fff',
               borderRadius: '8px',
               cursor: 'pointer',
               transition: 'all 0.3s ease',
               border: '1px solid #e8e8e8',
               minHeight: '180px',
             }}
             bodyStyle={{ padding: '16px' }}
             className="metric-card"
             hoverable
             onClick={() => navigate("/fornecedores")}
           >
             <div style={{ marginBottom: '2px' }}>
               <h4 style={{ margin: 0, fontWeight: 500, fontSize: '16px' }}>Maturidade</h4>
               <span style={{ color: '#888', fontSize: '12px' }}>Fornecedores</span>
             </div>
             
             <div style={{ 
               fontSize: '44px', 
               color: '#5c4ab5', 
               fontWeight: 500,
               marginBottom: '8px',
               lineHeight: '1',
               display: 'flex',
               alignItems: 'baseline',
             }}>
               {safeGet(dashboardStats?.fornecedores.percentualMaturidade, 0)}
               <span style={{ fontSize: '24px', color: '#ccc' }}>%</span>
             </div>
             
             <Tag 
               color={getConformidadeColor(safeGet(dashboardStats?.fornecedores.nivelConformidade, 'baixo'))}
               style={{ 
                 borderRadius: '4px',
                 padding: '0px 8px',
                 margin: 0,
                 fontSize: '12px',
                 marginTop: '4px'
               }}
             >
               Conformidade {getConformidadeText(safeGet(dashboardStats?.fornecedores.nivelConformidade, 'baixo'))}
             </Tag>
           </Card>
         </div>
       </Col>
       
       {/* Coluna 2: MAPEAMENTOS DINÂMICO e Ação com fornecedores */}
       <Col xs={24} sm={24} md={8} lg={8}>
         <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
           <Card 
             style={{ 
               background: '#fff',
               borderRadius: '8px',
               cursor: 'pointer',
               transition: 'all 0.3s ease',
               border: '1px solid #e8e8e8',
               minHeight: '180px',
               opacity: loadingMapeamentos ? 0.7 : 1,
             }}
             bodyStyle={{ padding: '16px' }}
             className="metric-card"
             hoverable
             onClick={() => navigate("/dashboard/mapeamentos")}
           >
             <div style={{ marginBottom: '2px' }}>
               <h4 style={{ margin: 0, fontWeight: 500, fontSize: '16px' }}>
                 Mapeamentos
                 {loadingMapeamentos && <Spin size="small" style={{ marginLeft: 8 }} />}
               </h4>
               <span style={{ color: '#888', fontSize: '12px' }}>Processos LGPD</span>
             </div>
             
             <div style={{ 
               fontSize: '44px', 
               color: '#5c4ab5', 
               fontWeight: 500,
               marginBottom: '8px',
               lineHeight: '1',
               display: 'flex',
               alignItems: 'baseline',
             }}>
               {mapeamentosStats ? safeGet(mapeamentosStats.questionariosConcluidos, 0) : 0}
               <span style={{ fontSize: '24px', color: '#ccc' }}>
                 /{mapeamentosStats ? safeGet(mapeamentosStats.totalQuestionarios, 0) : 0}
               </span>
             </div>
             
             <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
               <Tag 
                 color={mapeamentosStats ? getMapeamentosStatusColor(
                   safeGet(mapeamentosStats.taxaConclusao, 0)
                 ) : 'gray'}
                 style={{ 
                   borderRadius: '4px',
                   padding: '0px 8px',
                   margin: 0,
                   fontSize: '12px'
                 }}
               >
                 {mapeamentosStats ? getMapeamentosStatusText(
                   safeGet(mapeamentosStats.taxaConclusao, 0)
                 ) : 'Aguardando'}
               </Tag>
               {mapeamentosStats && mapeamentosStats.totalTitulares > 0 && (
                 <span style={{ fontSize: '12px', color: '#888' }}>
                   {mapeamentosStats.totalTitulares} titular{mapeamentosStats.totalTitulares > 1 ? 'es' : ''}
                 </span>
               )}
               {mapeamentosStats && mapeamentosStats.taxaConclusao > 0 && (
                 <span style={{ fontSize: '12px', color: '#888' }}>
                   {mapeamentosStats.taxaConclusao}% concluído
                 </span>
               )}
             </div>
           </Card>

           {/* Card Ação com fornecedores */}
           <Card 
             style={{ 
               background: '#fff',
               borderRadius: '8px',
               cursor: 'pointer',
               transition: 'all 0.3s ease',
               border: '1px solid #e8e8e8',
               minHeight: '180px',
             }}
             bodyStyle={{ padding: '16px' }}
             className="metric-card"
             hoverable
             onClick={() => navigate("/fornecedores")}
           >
             <div style={{ marginBottom: '2px' }}>
               <h4 style={{ margin: 0, fontWeight: 500, fontSize: '16px' }}>Ação com fornecedores</h4>
               <span style={{ color: '#888', fontSize: '12px' }}>Questionários</span>
             </div>
             
             <div style={{ 
               fontSize: '44px', 
               color: '#5c4ab5', 
               fontWeight: 500,
               marginBottom: '8px',
               lineHeight: '1',
               display: 'flex',
               alignItems: 'baseline',
             }}>
               {safeGet(dashboardStats?.fornecedores.questionariosRespondidos, 0)}
               <span style={{ fontSize: '24px', color: '#ccc' }}>
                 /{safeGet(dashboardStats?.fornecedores.totalCadastros, 0)}
               </span>
             </div>
             
             <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
               <Tag 
                 color={
                   safeGet(dashboardStats?.fornecedores.questionariosRespondidos, 0) === safeGet(dashboardStats?.fornecedores.totalCadastros, 0) 
                     ? "green" 
                     : safeGet(dashboardStats?.fornecedores.questionariosRespondidos, 0) > 0 
                       ? "blue" 
                       : "red"
                 }
                 style={{ 
                   borderRadius: '4px',
                   padding: '0px 8px',
                   margin: 0,
                   fontSize: '12px'
                 }}
               >
                 {safeGet(dashboardStats?.fornecedores.questionariosRespondidos, 0) === safeGet(dashboardStats?.fornecedores.totalCadastros, 0) 
                   ? "Concluído"
                   : safeGet(dashboardStats?.fornecedores.questionariosRespondidos, 0) > 0 
                     ? "Em andamento" 
                     : "Pendente"}
               </Tag>
               {safeGet(dashboardStats?.fornecedores.enviosPendentes, 0) > 0 && (
                 <span style={{ fontSize: '12px', color: '#888' }}>
                   {safeGet(dashboardStats?.fornecedores.enviosPendentes, 0)} pendente(s)
                 </span>
               )}
             </div>
           </Card>
         </div>
       </Col>
       
       {/* Timeline de Reuniões - Todas as reuniões */}
       <Col xs={24} sm={24} md={8} lg={8}>
         <Card 
           style={{ 
             background: '#fff',
             borderRadius: '8px',
             cursor: 'pointer',
             transition: 'all 0.3s ease',
             border: '1px solid #e8e8e8',
             height: '100%'
           }}
           bodyStyle={{ padding: '16px', height: '100%' }}
           className="metric-card"
           hoverable
           onClick={() => navigate("/dashboard-cppd")}
         >
           <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
             <div style={{ marginBottom: '16px' }}>
               <h4 style={{ margin: 0, fontWeight: 600, fontSize: '16px' }}>
                 CPPD - Timeline de Reuniões
               </h4>
               <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                 {safeGet(dashboardStats?.cppd.membrosCount, 0)} membros • {' '}
                 {safeGet(dashboardStats?.cppd.totalReunioes, 0)} reuniões • {' '}
                 {safeGet(dashboardStats?.cppd.estatisticasParticipacao?.mediaTaxaParticipacao, 0)}% participação média
               </div>
             </div>
             
             <div style={{ flex: 1, paddingRight: '8px', maxHeight: '320px', overflowY: 'auto' }}>
               {(() => {
                 const todasReunioes = [];
                 if (dashboardStats?.cppd.proximasReunioes) {
                   dashboardStats.cppd.proximasReunioes.forEach(reuniao => {
                     todasReunioes.push({
                       id: reuniao.id,
                       nome: reuniao.nome,
                       data: new Date(reuniao.data),
                       status: reuniao.status,
                       responsavel: reuniao.responsavel,
                       tipo: 'futura'
                     });
                   });
                 }
                 
                 // Simular reuniões passadas baseadas nos dados disponíveis
                 const reunioesConcluidas = safeGet(dashboardStats?.cppd.reunioesConcluidas, 0);
                 const reunioesEmAndamento = safeGet(dashboardStats?.cppd.reunioesEmAndamento, 0);
                 
                 // Adicionar reuniões concluídas (simuladas)
                 for (let i = 0; i < reunioesConcluidas; i++) {
                   todasReunioes.push({
                     id: `concluida-${i}`,
                     nome: `Reunião ${['Ordinária', 'Extraordinária', 'de Análise', 'de Revisão', 'de Planejamento'][i % 5]} ${Math.floor(i/5) + 1}`,
                     data: new Date(Date.now() - (i + 1) * 15 * 24 * 60 * 60 * 1000), // A cada 15 dias atrás
                     status: 'Realizada',
                     responsavel: ['João Silva', 'Maria Santos', 'Pedro Costa', 'Ana Lima'][i % 4],
                     tipo: 'concluida'
                   });
                 }
                 
                 // Adicionar reuniões em andamento (simuladas)
                 for (let i = 0; i < reunioesEmAndamento; i++) {
                   todasReunioes.push({
                     id: `andamento-${i}`,
                     nome: `Reunião em Andamento ${i + 1}`,
                     data: new Date(Date.now() - i * 2 * 24 * 60 * 60 * 1000), // Últimos dias
                     status: 'Em andamento',
                     responsavel: ['Carlos Oliveira', 'Lucia Fernandes'][i % 2],
                     tipo: 'andamento'
                   });
                 }
                 
                 // Ordenar por data (mais recentes/futuras primeiro)
                 todasReunioes.sort((a, b) => b.data.getTime() - a.data.getTime());
                 
                 return todasReunioes.length > 0 ? (
                   todasReunioes.map((reuniao, index) => {
                     const agora = new Date();
                     const isFutura = reuniao.data > agora;
                     const isHoje = reuniao.data.toDateString() === agora.toDateString();
                     const isRecente = Math.abs(reuniao.data.getTime() - agora.getTime()) < 7 * 24 * 60 * 60 * 1000; // Últimos 7 dias
                     
                     const statusInfo = getStatusInfo(reuniao.status);
                     
                     // Determinar cor e ícone baseado no status e tempo
                     let corPrimaria = statusInfo.color === 'green' ? '#52c41a' : 
                                     statusInfo.color === 'blue' ? '#1890ff' : 
                                     statusInfo.color === 'orange' ? '#faad14' : '#d9d9d9';
                     
                     let icone = statusInfo.color === 'green' ? '✅' : 
                               statusInfo.color === 'blue' ? '🔄' : 
                               isFutura ? '📅' : '⏳';
                     
                     // Destaque especial para reuniões de hoje
                     if (isHoje) {
                       corPrimaria = '#722ed1';
                       icone = '🎯';
                     }
                     
                     return (
                       <div key={reuniao.id} style={{ 
                         marginBottom: index < todasReunioes.length - 1 ? "12px" : "0", 
                         display: 'flex', 
                         alignItems: 'flex-start',
                         padding: '10px',
                         borderRadius: '8px',
                         backgroundColor: isHoje ? '#f9f0ff' : 
                                         isRecente ? '#f6ffed' : 
                                         isFutura ? '#fff7e6' : '#fafafa',
                         border: isHoje ? '2px solid #722ed1' : 
                               isRecente ? '1px solid #b7eb8f' : 
                               '1px solid #e8e8e8',
                         transition: 'all 0.2s ease',
                         cursor: 'pointer'
                       }}
                       onClick={(e) => {
                         e.stopPropagation();
                         navigate("/dashboard-cppd");
                       }}
                       >
                         {/* Ícone e linha conectora */}
                         <div style={{ 
                           display: 'flex',
                           flexDirection: 'column',
                           alignItems: 'center',
                           marginRight: "12px",
                           minWidth: '20px'
                         }}>
                           <div style={{ 
                             width: '20px', 
                             height: '20px', 
                             borderRadius: '50%', 
                             background: corPrimaria,
                             display: 'flex',
                             alignItems: 'center',
                             justifyContent: 'center',
                             fontSize: '10px',
                             color: '#fff',
                             fontWeight: 'bold',
                             marginBottom: '2px'
                           }}>
                             {icone}
                           </div>
                           {/* Linha conectora vertical */}
                           {index < todasReunioes.length - 1 && (
                             <div style={{
                               width: '2px',
                               height: '16px',
                               background: '#e8e8e8',
                               marginTop: '2px'
                             }} />
                           )}
                         </div>
                         
                         {/* Conteúdo da reunião */}
                         <div style={{ flex: 1, minWidth: 0 }}>
                           <div style={{ 
                             fontSize: '13px', 
                             fontWeight: isHoje ? 600 : isRecente ? 500 : 400,
                             color: isHoje ? '#722ed1' : isRecente ? '#52c41a' : '#262626',
                             marginBottom: '3px',
                             overflow: 'hidden',
                             textOverflow: 'ellipsis',
                             whiteSpace: 'nowrap'
                           }}>
                             {reuniao.nome}
                             {isHoje && <span style={{ marginLeft: '6px', fontSize: '10px' }}>• HOJE</span>}
                           </div>
                           
                           <div style={{ 
                             fontSize: '11px', 
                             color: '#8c8c8c', 
                             marginBottom: '4px'
                           }}>
                             {reuniao.data.toLocaleDateString('pt-BR', {
                               day: '2-digit',
                               month: '2-digit',
                               year: '2-digit',
                               weekday: 'short'
                             })} às {reuniao.data.toLocaleTimeString('pt-BR', {
                               hour: '2-digit',
                               minute: '2-digit'
                             })}
                           </div>
                           
                           <div style={{ 
                             fontSize: '11px', 
                             color: '#8c8c8c',
                             marginBottom: '4px'
                           }}>
                             Responsável: {reuniao.responsavel}
                           </div>
                           
                           <div style={{ 
                             display: 'flex', 
                             justifyContent: 'space-between', 
                             alignItems: 'center' 
                           }}>
                             <span style={{ 
                               fontSize: '10px', 
                               color: isFutura ? '#fa8c16' : '#bfbfbf',
                               fontStyle: 'italic'
                             }}>
                               {isFutura ? 'Agendada' : 
                               Math.floor((agora.getTime() - reuniao.data.getTime()) / (24 * 60 * 60 * 1000))} dias atrás
                             </span>
                             <Tag 
                               color={corPrimaria} 
                               style={{ 
                                 margin: 0, 
                                 fontSize: '9px', 
                                 padding: '1px 6px',
                                 borderRadius: '8px',
                                 lineHeight: '16px'
                               }}
                             >
                               {statusInfo.text}
                             </Tag>
                           </div>
                         </div>
                       </div>
                     );
                   })
                 ) : (
                   <div style={{ 
                     textAlign: 'center', 
                     color: '#888', 
                     padding: '40px 20px',
                     display: 'flex',
                     flexDirection: 'column',
                     alignItems: 'center',
                     justifyContent: 'center',
                     height: '100%'
                   }}>
                     <Empty 
                       description="Nenhuma reunião agendada"
                       image={Empty.PRESENTED_IMAGE_SIMPLE}
                       style={{ margin: 0 }}
                     />
                     <Button 
                       type="link" 
                       size="small" 
                       onClick={(e) => {
                         e.stopPropagation();
                         navigate("/dashboard/governanca/cppd");
                       }}
                       style={{ marginTop: 8, padding: 0 }}
                     >
                       Agendar primeira reunião →
                     </Button>
                   </div>
                 );
               })()}
             </div>
             
             {/* Footer com resumo das reuniões */}
             <div style={{ 
               borderTop: '1px solid #f0f0f0',
               paddingTop: '8px',
               marginTop: '8px'
             }}>
               <div style={{ 
                 display: 'flex', 
                 justifyContent: 'space-between',
                 fontSize: '10px',
                 color: '#8c8c8c'
               }}>
                 <span style={{ color: '#52c41a' }}>
                   ✅ {safeGet(dashboardStats?.cppd.reunioesConcluidas, 0)} concluídas
                 </span>
                 <span style={{ color: '#1890ff' }}>
                   🔄 {safeGet(dashboardStats?.cppd.reunioesEmAndamento, 0)} em andamento
                 </span>
                 <span style={{ color: '#faad14' }}>
                   📅 {safeGet(dashboardStats?.cppd.reunioesPendentes, 0)} pendentes
                 </span>
               </div>
             </div>
           </div>
         </Card>
       </Col>
     </Row>

     {/* Tabela de Chamados */}
     <div>
       <div style={{ 
         display: 'flex', 
         justifyContent: 'space-between', 
         alignItems: 'center',
         marginBottom: '16px' 
       }}>
         <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Chamados</h3>
         <Button 
           type="primary" 
           onClick={handleShowCriarTarefa}
           style={{ background: '#7c3aed', borderColor: '#7c3aed' }}
         >
           Criar chamado
         </Button>
       </div>
       
       <Table
         dataSource={chamadosWithNumbers}
         columns={chamadosColumns}
         locale={{
           emptyText: (
             <Empty
               style={{ marginTop: "2rem" }}
               description={<span>Nenhum chamado encontrado</span>}
             />
           ),
         }}
         rowKey="_id"
         size="middle"
       />
     </div>

     {/* Modais */}
     <CriarTarefa
       showModal={showCriarTarefa}
       handleShowModal={handleShowCriarTarefa}
       tipo="chamado"
     />

     <Task setShowModal={setShowModal} showModal={showModal} />

     <Modal
       title={""}
       open={showModalQuestionario}
       footer={null}
       width={"500px"}
       onCancel={() => setShowModalQuestionario(false)}
     >
       <div
         style={{
           display: "flex",
           flexDirection: "column",
           justifyContent: "center",
           alignItems: "center",
         }}
       >
         <br />
         <br />
         <br />

         <FileDoneOutlined style={{ fontSize: "80px", color: "#32E6BE" }} />
         <br />
         <h2>Questionário disponível!</h2>
         <br />
         <br />

         <Button
           onClick={() => {
             const desiredRoute = localStorage.getItem("@desiredroute");
             if (desiredRoute) {
               setShowModalQuestionario(false);
               navigate(desiredRoute);
               localStorage.removeItem("@desiredroute");
             }
           }}
           type="text"
           style={{ color: "#008C76" }}
         >
           Responder já <RightOutlined />
         </Button>
         <br />
         <br />
       </div>
     </Modal>

     {/* Estilos CSS */}
     <style>{`
       .metric-card:hover {
         box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
         transform: translateY(-1px);
       }
       
       .metric-card {
         transition: all 0.2s ease-in-out;
       }
       
       .ant-card-body {
         position: relative;
       }
       
       .metric-card .ant-card-body::after {
         content: '';
         position: absolute;
         top: 16px;
         right: 16px;
         width: 8px;
         height: 8px;
         border-radius: 50%;
         background: #52c41a;
         opacity: 0.6;
       }
       
       .metric-card:hover .ant-card-body::after {
         opacity: 1;
         animation: pulse 1.5s infinite;
       }
       
       @keyframes pulse {
         0% {
           transform: scale(1);
           opacity: 1;
         }
         50% {
           transform: scale(1.2);
           opacity: 0.7;
         }
         100% {
           transform: scale(1);
           opacity: 1;
         }
       }
       
       /* Scrollbar personalizado para lista de reuniões */
       .ant-card-body > div > div:last-child::-webkit-scrollbar {
         width: 4px;
       }
       
       .ant-card-body > div > div:last-child::-webkit-scrollbar-track {
         background: #f1f1f1;
         border-radius: 4px;
       }
       
       .ant-card-body > div > div:last-child::-webkit-scrollbar-thumb {
         background: #c1c1c1;
         border-radius: 4px;
       }
       
       .ant-card-body > div > div:last-child::-webkit-scrollbar-thumb:hover {
         background: #a8a8a8;
       }
     `}</style>
   </div>
 );
};

export default DashboardInicial;